import StateMachine from './StateMachine';

/**
 * ConnectionStateMachine - State machine for server WebSocket connection
 *
 * States:
 * - DISCONNECTED: Not connected
 * - CONNECTING: Attempting to connect
 * - AUTHENTICATING: Connected, authenticating
 * - CONNECTED: Fully connected and authenticated
 * - RECONNECTING: Attempting to reconnect
 * - FAILED: Connection failed (terminal state)
 *
 * Transitions:
 * - connect: DISCONNECTED -> CONNECTING
 * - authenticate: CONNECTING -> AUTHENTICATING
 * - authenticated: CONNECTING/AUTHENTICATING -> CONNECTED
 * - disconnect: CONNECTED -> DISCONNECTED
 * - fail: * -> FAILED
 * - reconnect: DISCONNECTED/FAILED -> RECONNECTING
 * - retry: RECONNECTING -> CONNECTING
 *
 * @example
 * const machine = new ConnectionStateMachine({
 *   onStateChange: (state, prevState) => {
 *     console.log(`Connection: ${prevState} -> ${state}`);
 *   }
 * });
 *
 * machine.transition('connect'); // DISCONNECTED -> CONNECTING
 * machine.transition('authenticated'); // CONNECTING -> CONNECTED
 */

export const CONNECTION_STATES = {
  DISCONNECTED: 'DISCONNECTED',
  CONNECTING: 'CONNECTING',
  AUTHENTICATING: 'AUTHENTICATING',
  CONNECTED: 'CONNECTED',
  RECONNECTING: 'RECONNECTING',
  FAILED: 'FAILED'
};

export const CONNECTION_TRANSITIONS = {
  CONNECT: 'connect',
  AUTHENTICATE: 'authenticate',
  AUTHENTICATED: 'authenticated',
  DISCONNECT: 'disconnect',
  FAIL: 'fail',
  RECONNECT: 'reconnect',
  RETRY: 'retry'
};

class ConnectionStateMachine extends StateMachine {
  /**
   * Create ConnectionStateMachine
   * @param {Object} [options] - Configuration options
   * @param {Function} [options.onStateChange] - State change callback
   * @param {Function} [options.logger=console] - Logger instance
   * @param {boolean} [options.enableDebug=false] - Enable debug logging
   */
  constructor(options = {}) {
    const config = {
      initial: CONNECTION_STATES.DISCONNECTED,
      states: {
        [CONNECTION_STATES.DISCONNECTED]: {
          onEnter: () => {
            if (options.onStateChange) {
              options.onStateChange(CONNECTION_STATES.DISCONNECTED, this.previousState);
            }
          },
          transitions: {
            [CONNECTION_TRANSITIONS.CONNECT]: CONNECTION_STATES.CONNECTING,
            [CONNECTION_TRANSITIONS.RECONNECT]: CONNECTION_STATES.RECONNECTING
          }
        },

        [CONNECTION_STATES.CONNECTING]: {
          onEnter: () => {
            if (options.onStateChange) {
              options.onStateChange(CONNECTION_STATES.CONNECTING, this.previousState);
            }
          },
          transitions: {
            [CONNECTION_TRANSITIONS.AUTHENTICATE]: CONNECTION_STATES.AUTHENTICATING,
            [CONNECTION_TRANSITIONS.DISCONNECT]: CONNECTION_STATES.DISCONNECTED,
            [CONNECTION_TRANSITIONS.FAIL]: CONNECTION_STATES.FAILED
          }
        },

        [CONNECTION_STATES.AUTHENTICATING]: {
          onEnter: () => {
            if (options.onStateChange) {
              options.onStateChange(CONNECTION_STATES.AUTHENTICATING, this.previousState);
            }
          },
          transitions: {
            [CONNECTION_TRANSITIONS.AUTHENTICATED]: CONNECTION_STATES.CONNECTED,
            [CONNECTION_TRANSITIONS.DISCONNECT]: CONNECTION_STATES.DISCONNECTED,
            [CONNECTION_TRANSITIONS.FAIL]: CONNECTION_STATES.FAILED
          }
        },

        [CONNECTION_STATES.CONNECTED]: {
          onEnter: () => {
            if (options.onStateChange) {
              options.onStateChange(CONNECTION_STATES.CONNECTED, this.previousState);
            }
          },
          transitions: {
            [CONNECTION_TRANSITIONS.DISCONNECT]: CONNECTION_STATES.DISCONNECTED,
            [CONNECTION_TRANSITIONS.FAIL]: CONNECTION_STATES.RECONNECTING
          }
        },

        [CONNECTION_STATES.RECONNECTING]: {
          onEnter: () => {
            if (options.onStateChange) {
              options.onStateChange(CONNECTION_STATES.RECONNECTING, this.previousState);
            }
          },
          transitions: {
            [CONNECTION_TRANSITIONS.CONNECT]: CONNECTION_STATES.CONNECTING,
            [CONNECTION_TRANSITIONS.RETRY]: CONNECTION_STATES.CONNECTING,
            [CONNECTION_TRANSITIONS.DISCONNECT]: CONNECTION_STATES.DISCONNECTED,
            [CONNECTION_TRANSITIONS.FAIL]: CONNECTION_STATES.FAILED
          }
        },

        [CONNECTION_STATES.FAILED]: {
          onEnter: () => {
            if (options.onStateChange) {
              options.onStateChange(CONNECTION_STATES.FAILED, this.previousState);
            }
          },
          transitions: {
            [CONNECTION_TRANSITIONS.RECONNECT]: CONNECTION_STATES.RECONNECTING,
            [CONNECTION_TRANSITIONS.DISCONNECT]: CONNECTION_STATES.DISCONNECTED
          }
        }
      },
      onTransition: (currentState, previousState, transition, context) => {
        if (options.enableDebug) {
          console.debug(`[ConnectionStateMachine] ${previousState} --[${transition}]--> ${currentState}`, context);
        }
      },
      logger: options.logger || console,
      enableDebug: options.enableDebug || false
    };

    super(config);

    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = options.maxReconnectAttempts || 10;
  }

  /**
   * Check if connection is active
   * @returns {boolean} True if connected or connecting
   */
  isActive() {
    return this.isIn(
      CONNECTION_STATES.CONNECTING,
      CONNECTION_STATES.AUTHENTICATING,
      CONNECTION_STATES.CONNECTED,
      CONNECTION_STATES.RECONNECTING
    );
  }

  /**
   * Check if connected
   * @returns {boolean} True if fully connected
   */
  isConnected() {
    return this.is(CONNECTION_STATES.CONNECTED);
  }

  /**
   * Check if connecting
   * @returns {boolean} True if in connecting state
   */
  isConnecting() {
    return this.isIn(
      CONNECTION_STATES.CONNECTING,
      CONNECTION_STATES.AUTHENTICATING,
      CONNECTION_STATES.RECONNECTING
    );
  }

  /**
   * Check if disconnected
   * @returns {boolean} True if disconnected or failed
   */
  isDisconnected() {
    return this.isIn(CONNECTION_STATES.DISCONNECTED, CONNECTION_STATES.FAILED);
  }

  /**
   * Attempt to reconnect
   * @returns {boolean} True if reconnect initiated
   */
  reconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.transition(CONNECTION_TRANSITIONS.FAIL);
      return false;
    }

    this.reconnectAttempts++;

    if (this.is(CONNECTION_STATES.DISCONNECTED) || this.is(CONNECTION_STATES.FAILED)) {
      return this.transition(CONNECTION_TRANSITIONS.RECONNECT);
    }

    return false;
  }

  /**
   * Reset reconnect attempts counter
   */
  resetReconnectAttempts() {
    this.reconnectAttempts = 0;
  }

  /**
   * Get connection status info
   * @returns {Object} Status information
   */
  getConnectionInfo() {
    return {
      state: this.getState(),
      previousState: this.getPreviousState(),
      isActive: this.isActive(),
      isConnected: this.isConnected(),
      isConnecting: this.isConnecting(),
      isDisconnected: this.isDisconnected(),
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts
    };
  }
}

// Export as both named and default for flexibility
export { ConnectionStateMachine };
export default ConnectionStateMachine;
